import type { DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/core";
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { clsx } from "clsx";
import {
  createContext,
  type FC,
  type PointerEventHandler,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type WheelEvent,
} from "react";
import { mergeProps, useFocusRing, useHover, useMove } from "react-aria";
import { useDispatch, useSelector } from "react-redux";

import { getRangeCenteredAt, getRangeLength, rangeEquals, shiftRange } from "@/app-utils/range.ts";
import { type Range } from "@/graphql.ts";
import { trackHeaderWidthChanged, viewRangeChanged } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_HEADER_MAX_WIDTH, TRACK_HEADER_MIN_WIDTH } from "@/settings/genome-viewer-settings.ts";
import { AXIS_HEIGHT, MIN_VIEW_RANGE_SIZE, SCALE_FACTOR_MOUSE_WHEEL_ZOOM } from "@/settings/settings.js";

import { useGenomeViewerData } from "./_mod.js";
import { GenomeViewerSelectionRectangle } from "./GenomeViewerSelectionRectangle.js";
import style from "./styles/GenomeViewerTracksList.module.scss";

const GenomeViewerTrackHeaderResizeHandle: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { layout } = useGenomeViewerData();
  const clamp = (pos: number) => Math.min(Math.max(pos, TRACK_HEADER_MIN_WIDTH), TRACK_HEADER_MAX_WIDTH);
  const [position, setPosition] = useState(layout.headerWidth);
  const { focusProps, isFocusVisible } = useFocusRing();
  const { hoverProps, isHovered } = useHover({});
  const [isMoving, setMoving] = useState(false);
  const { moveProps } = useMove({
    onMoveStart() {
      setMoving(true);
    },
    onMove(e) {
      setPosition((pos) => {
        if (e.pointerType === "keyboard") {
          pos = clamp(pos);
        }
        pos += e.deltaX;
        return Math.round(pos);
      });
    },
    onMoveEnd() {
      setPosition((pos) => {
        const x = clamp(pos);
        dispatch(trackHeaderWidthChanged(Math.round(x)));
        return Math.round(x);
      });
      setMoving(false);
    },
  });

  useEffect(() => {
    setPosition(layout.headerWidth);
  }, [layout.headerWidth]);

  return (
    <div
      {...mergeProps(moveProps, focusProps, hoverProps)}
      tabIndex={0}
      className={clsx(style.track_header_grab, {
        [style.track_header_grab_active]: isFocusVisible || isHovered || isMoving,
      })}
      style={{ left: clamp(position) }}
    />
  );
};

enum RangeSelectionMode {
  Zoom = 1,
  Select = 2,
}

const DndActiveContext = createContext<{ isDndActive: boolean; setDndActive(active: boolean): void }>({
  isDndActive: true,
  setDndActive: () => {
    /* empty */
  },
});

const DRAG_DISTANCE_ACTIVATION_CONSTRAINT = 10;

const GenomeViewerInteractiveSurface: FC<PropsWithChildren<{ closeTooltip: () => void }>> = ({
  children,
  closeTooltip,
}) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { layout, range, contig, baseWidth } = useGenomeViewerData();

  const [selectionRange, setSelectionRange] = useState<Range>();
  const selectionMode = useRef<RangeSelectionMode>(undefined);

  // sync with the selected range stored in redux and updated by other components e.g. interactive tooltips
  const selectedRange = useSelector<IAppState, Range>((state) => state.genomeBrowser.selectedRange);
  useEffect(() => {
    if (!rangeEquals(selectionRange, selectedRange)) {
      setSelectionRange(selectedRange);
    }
  }, [selectedRange]);

  const [isDndActive, setDndActive] = useState(true);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: DRAG_DISTANCE_ACTIVATION_CONSTRAINT },
  });
  const sensors = useSensors(pointerSensor);

  const viewToBasePosition = useCallback<(x: number) => number>(
    (x) => range.start + Math.floor(x / baseWidth),
    [baseWidth, range.start],
  );

  const createRangeInView = useCallback(
    (pos1: number, pos2: number): Range => {
      return {
        start: Math.max(Math.min(pos1, pos2), range.start),
        stop: Math.min(Math.max(pos1, pos2), range.stop),
      };
    },
    [range],
  );

  const createRangeOnContig = useCallback(
    (pos1: number, pos2: number): Range => {
      return {
        start: Math.max(Math.min(pos1, pos2), 1),
        stop: Math.min(Math.max(pos1, pos2), contig.length),
      };
    },
    [contig.length],
  );

  // Register the current mouse position before the drag event => Selection modes.
  const mousePosition = useRef(-1);
  const handlePointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left - layout.headerWidth;
      mousePosition.current = x > 0 ? x : 0;
    },
    [layout.headerWidth],
  );

  const handlePointerUp = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      // Reset the rect selection if not dragged (see sensors and DRAG_DISTANCE_ACTIVATION_CONSTRAINT)
      // Compare from PointerDown position.
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left - layout.headerWidth;
      if (Math.abs(mousePosition.current - x) < DRAG_DISTANCE_ACTIVATION_CONSTRAINT) {
        setSelectionRange(undefined);
      }
    },
    [layout.headerWidth],
  );

  const handleMouseWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        closeTooltip();

        // deltaY: zoom in (wheel up) -> negative, zoom out (wheel down) -> positive
        const finalFactor = event.deltaY > 0 ? SCALE_FACTOR_MOUSE_WHEEL_ZOOM : 1 / SCALE_FACTOR_MOUSE_WHEEL_ZOOM;
        // number of pixels from the left border of the browser window to the left border of the first displayed nucleotide (aka base)
        const leftOffset = event.currentTarget.getBoundingClientRect().left + layout.headerWidth;
        // the relative pixel position in the track
        const xPos = Math.min(Math.max(0, event.clientX - leftOffset), layout.trackWidth);

        // the index of the base that is currently below the mouse pinter
        const currentBase = viewToBasePosition(xPos);

        //// Find range for the new zoom level

        // Calculate the new range based on an approximate value for newPxPerBp
        let newPxPerBp = baseWidth / finalFactor;
        let newRange = getNewRange(newPxPerBp, currentBase, xPos, layout.trackWidth);

        // Above calculation approximates the baseWidthPx from the scaling factor
        // but the actual space for a base is different because the display will show complete bases while the approximate
        // width would produce a partially displayed base at the end or beginning of the display range.
        // Let's recalculate everything with a better value for newPxPerBp
        newPxPerBp = layout.trackWidth / (newRange.stop - newRange.start + 1);
        newRange = getNewRange(newPxPerBp, currentBase, xPos, layout.trackWidth);

        // Above calculations get the length of range for the final scale right, but the current base
        // might be no longer under the mouse.
        // To compensate we shift the displayed range by full bases until the current base is under the mouse again.
        //
        // Check if the current xPos of the mouse would still be over the correct base in the new range
        const baseAtMx = Math.floor(xPos / newPxPerBp) + newRange.start;
        // calculate the number of bases that we need to shift the new range so that the base is below the mouse again
        const deltaBp = currentBase - baseAtMx;
        // shift the new range by the deltaBp
        newRange = { start: newRange.start + deltaBp, stop: newRange.stop + deltaBp };

        // check if newRange is still within limits
        if (newRange.stop - newRange.start + 1 < MIN_VIEW_RANGE_SIZE) {
          return;
        }

        dispatch(viewRangeChanged(newRange));
      }
    },
    [baseWidth, dispatch, layout.headerWidth, layout.trackWidth, viewToBasePosition],
  );

  const beforeDragRange = useRef<Range>(range);
  const onDragStart = useCallback<(event: DragStartEvent) => void>(() => {
    closeTooltip();
    //! Selection modes are set via keydown/keyup event

    // Save the current range before the drag because dnd-kit gives a delta from start (and not betwwen mousemoveevent)
    beforeDragRange.current = range;
  }, [range]);

  const onDrag = useCallback<(event: DragMoveEvent) => void>(
    (event) => {
      // Delta is from dragstart. How many bases did we move so far (can be negative)
      const baseDisplacement = Math.round(event.delta.x / baseWidth);

      // Nothing to do, probably a usual click event
      //! Currently won't ever happen, see sensors + DRAG_DISTANCE_ACTIVATION_CONSTRAINT
      // But we keep it in case of DRAG_DISTANCE_ACTIVATION_CONSTRAINT = 0
      if (baseDisplacement === 0) return;

      if (selectionMode.current == undefined) {
        // Handle track drag update
        let newRange = shiftRange(beforeDragRange.current, baseDisplacement * -1);
        newRange = getRangeCenteredAt(newRange, getRangeLength(range), contig.length);

        // should be always true as baseDisplacement is always != zero
        if (!rangeEquals(newRange, range)) {
          dispatch(viewRangeChanged(newRange));
        }
      } else {
        // Handle selection/zoom drag update
        setSelectionRange(
          createRangeInView(
            viewToBasePosition(mousePosition.current),
            viewToBasePosition(mousePosition.current + event.delta.x),
          ),
        );
      }
    },
    [baseWidth, contig.length, createRangeInView, dispatch, viewToBasePosition, range],
  );

  const onDragEnd = useCallback<(event: DragEndEvent) => void>(
    (event) => {
      // If currently zooming and not a click. If you select before, press Control and click to select another entity,
      // it may be interpreted as a zoom on a Control+click event
      if (selectionMode.current === RangeSelectionMode.Zoom && event.delta.x !== 0) {
        dispatch(viewRangeChanged(selectionRange));
        setSelectionRange(undefined); // Clean selection after zooming
      }

      // Usual click: clear the selection state because this was not a real drag event
      //! Currently won't ever happen, see sensors + DRAG_DISTANCE_ACTIVATION_CONSTRAINT
      // But we keep it in case of DRAG_DISTANCE_ACTIVATION_CONSTRAINT = 0
      if (event.delta.x === 0) {
        setSelectionRange(undefined);
      }
    },
    [dispatch, selectionRange],
  );

  // Load: Register Keyboard events
  useEffect(() => {
    const downCallback = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && selectionRange == undefined) {
        selectionMode.current = RangeSelectionMode.Zoom;
      } else if (event.shiftKey) {
        selectionMode.current = RangeSelectionMode.Select;
      }
    };

    const upCallback = () => {
      if (selectionMode.current === RangeSelectionMode.Zoom) {
        // Allow to "cancel" the zoom by just releasing the Control key
        setSelectionRange(undefined);
      }

      selectionMode.current = undefined;
    };

    window.addEventListener("keydown", downCallback);
    window.addEventListener("keyup", upCallback);

    return () => {
      // Clean keyboard events
      window.removeEventListener("keydown", downCallback);
      window.removeEventListener("keyup", upCallback);
    };
  }, [selectionRange]);

  // Reset selection on contig change
  useEffect(() => {
    setSelectionRange(undefined);
  }, [contig]);

  // Add some grey on the right side of the tracks if the scrollbar is visible (like on Windows)
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    setScrollbarWidth((rect?.width ?? layout.viewWidth) - layout.viewWidth);
  }, [layout.viewWidth]);

  return (
    <DndActiveContext.Provider value={{ isDndActive, setDndActive }}>
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragMove={onDrag} onDragEnd={onDragEnd}>
        <Draggable>
          <div
            ref={ref}
            className={style.container}
            style={{
              paddingTop: AXIS_HEIGHT,
              boxShadow: `inset ${scrollbarWidth * -1}px 0 0 #f1f1f1`, // same color as the Windows 10 scrollbars background
            }}
            onWheel={handleMouseWheel}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
          >
            <GenomeViewerTrackHeaderResizeHandle />
            {children}
            {/*  the selection rectangle after the tracks so that the tracks do not catch the event over selection handlebars first */}
            <GenomeViewerSelectionRectangle
              selectionRange={selectionRange}
              selectionMode={selectionMode.current}
              modifySelectionRange={setSelectionRange}
              viewToBasePosition={viewToBasePosition}
              createRangeOnContig={createRangeOnContig}
            />
          </div>
        </Draggable>
      </DndContext>
    </DndActiveContext.Provider>
  );
};

const Draggable: FC<PropsWithChildren> = (props) => {
  const { isDndActive } = useContext(DndActiveContext);
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: "genome-viewer-interactive-surface-draggable",
    disabled: !isDndActive,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ display: "flex", height: "100%", cursor: "default", outline: "none", flex: 1, overflow: "hidden" }}
    >
      {props.children}
    </div>
  );
};

function getNewRange(newPxPerBp: number, currentBase: number, xPos: number, trackWidth: number): Range {
  const newLeftBp = Math.floor(xPos / newPxPerBp);
  const newPxOnRightSide = trackWidth - (newLeftBp + 1) * newPxPerBp;
  const newRightBp = Math.floor(newPxOnRightSide / newPxPerBp);
  return { start: currentBase - newLeftBp, stop: currentBase + newRightBp };
}

export { DndActiveContext, GenomeViewerInteractiveSurface, RangeSelectionMode };
